/* eslint-disable max-len */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import SyntaxHighlighter from "react-syntax-highlighter";
import { docco } from "react-syntax-highlighter/dist/esm/styles/hljs";
import { Check, ChevronDown, ChevronUp, Copy, ExternalLink, HelpCircle } from "lucide-react";
import { Badge } from "../../../../components/ui/badge";
import { Button } from "../../../../components/ui/button";
import { cn } from "../../../../lib/utils";
import useCheckEvents from "../../../../hooks/useCheckEvents";
import { updateIntegration } from "../../../../services/integrationEndpoint";
import { updateWorkspace } from "../../../../services/workspace/workspaceEndpoints";
import { queryClient } from "../../../../hooks/QueryHooks";

type Props = {
  data: {
    apiKey: any,
    workspace: any,
  },
  steps: {
    script: string,
    title: string,
    script2?: string,
    desc: string,
  }[] | [],
  api?: boolean;
  integration: any;
};

const DOC_LINKS = [
  "https://docs.hyperengage.io/quickstart/install",
  "https://docs.hyperengage.io/quickstart/identify",
  "https://docs.hyperengage.io/quickstart/identify",
  "https://docs.hyperengage.io/quickstart/track",
];

const formatEventPreview = (event: Record<string, unknown>) => Object.entries(event || {})
  .map(([key, value]) => `${key}: "${value}"`)
  .join("\n");

const CopyButton = ({ text }: { text: string }) => {
  const [copied, setCopied] = useState(false);

  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      className="h-7 gap-1.5 text-[12px]"
      onClick={() => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }}
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? "Copied" : "Copy"}
    </Button>
  );
};

const CodeBlock = ({ code, language = "javascript" }: { code: string; language?: string }) => (
  <div className="space-y-2">
    <div className="overflow-x-auto rounded-lg border border-border bg-quaternary">
      <SyntaxHighlighter showLineNumbers language={language} customStyle={{ fontSize: "13px", margin: 0 }} style={docco}>
        {code}
      </SyntaxHighlighter>
    </div>
    <CopyButton text={code} />
  </div>
);

const WaitingEventCard = ({ text }: { text: string }) => (
  <div className="rounded-lg border border-primary/20 bg-primary/[0.03] p-4">
    <div className="flex items-center gap-2">
      <span className="relative flex h-2.5 w-2.5">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-primary" />
      </span>
      <p className="text-[12px] text-muted-foreground">{text}</p>
    </div>
  </div>
);

const NativeSteps = ({ data, steps, api, integration }: Props) => {
  const navigate = useNavigate();

  const checkEvents: any = useCheckEvents({
    token: data.apiKey?.id,
    integrationName: "native",
    workspaceId: data.workspace?.id,
  });

  const [activeStep, setActiveStep] = useState(0);
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set([0]));

  const toggleStep = (index: number) => {
    const next = new Set(expandedSteps);
    if (next.has(index)) next.delete(index);
    else next.add(index);
    setExpandedSteps(next);
    setActiveStep(index);
  };

  const handleNext = () => {
    if (activeStep >= steps.length - 1) return;
    const nextStep = activeStep + 1;
    setActiveStep(nextStep);
    setExpandedSteps((prev) => new Set(prev).add(nextStep));
  };

  const canContinue = Boolean(checkEvents?.data?.identify?.length && checkEvents?.data?.track?.length);

  const handleContinue = async () => {
    if (!data?.workspace?.id || !integration?.id) return;

    await updateIntegration({
      created_by_user_id: integration?.created_by_user_id,
      source_name: integration?.source_name || "native",
      id: integration.id,
      workspace_id: data.workspace?.id,
      setup_complete: true,
      mapping_complete: true,
      events_received: true,
      is_active: true,
    });

    await updateWorkspace({
      workspace_id: data.workspace?.id,
      setup_complete: true,
    });
    // RootPage decides redirects off the DB-backed workspace query; refresh it
    // so a stale "incomplete" read can't bounce the user back to onboarding.
    queryClient.invalidateQueries(["workspace"]);

    navigate(`/home/${data.workspace?.unique_identifier}/headquarters`, { replace: true, state: { setupComplete: true } });
  };

  if (!steps?.length) {
    return (
      <div className="flex min-h-[320px] items-center justify-center rounded-lg border border-border bg-card p-8 text-lg text-muted-foreground">
        Coming Soon...
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-[15px] font-semibold text-foreground">Setup checklist</h2>
        <p className="mt-1 text-[13px] text-muted-foreground">
          Complete each step and wait for identify plus track events before finalizing setup.
        </p>
      </div>

      <div>
        {steps.map((step, index) => {
          const isActive = index === activeStep;
          const isCompleted = index < activeStep;
          const isExpanded = expandedSteps.has(index);
          const isLast = index === steps.length - 1;

          return (
            <div key={`${step.title}-${index}`} className="relative flex gap-4">
              <div className="flex flex-shrink-0 flex-col items-center">
                <div
                  className={cn(
                    "flex h-6 w-6 items-center justify-center rounded-full border-2 transition-colors",
                    isCompleted
                      ? "border-primary bg-primary"
                      : isActive
                        ? "border-primary bg-card"
                        : "border-border bg-card",
                  )}
                >
                  {isCompleted ? <Check className="h-3 w-3 text-primary-foreground" /> : null}
                  {isActive && !isCompleted ? <div className="h-2 w-2 rounded-full bg-primary" /> : null}
                </div>
                {!isLast ? (
                  <div className={cn("min-h-[24px] w-px flex-1", isCompleted ? "bg-primary" : "bg-border")} />
                ) : null}
              </div>

              <div className={cn("min-w-0 flex-1", !isLast ? "pb-6" : "")}> 
                <button
                  type="button"
                  onClick={() => toggleStep(index)}
                  className="group flex w-full items-start justify-between text-left"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className={cn("text-sm font-medium", isActive ? "text-primary" : "text-foreground")}>
                        {step.title}
                      </h3>
                      {index === 1 ? (
                        <Badge variant="statusPill" status="open" className="text-[10px]">
                          optional
                        </Badge>
                      ) : null}
                    </div>
                    <p className="mt-0.5 pr-4 text-[13px] leading-relaxed text-muted-foreground">
                      {step.desc}
                      {index === 1 ? (
                        <button type="button" className="ml-1 inline-flex text-primary hover:underline">
                          <HelpCircle className="h-3 w-3" />
                        </button>
                      ) : null}
                    </p>
                  </div>
                  <div className="mt-1 flex-shrink-0 text-muted-foreground">
                    {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </div>
                </button>

                {isExpanded ? (
                  <div className="mt-4 space-y-4">
                    {api && index === 0 ? (
                      <div className="space-y-3 rounded-lg border border-border p-3">
                        <div className="flex items-center gap-2 text-[13px]">
                          <span className="font-medium text-foreground">Workspace Identifier:</span>
                          <code className="rounded border border-border bg-muted px-2 py-1 font-mono text-[12px] text-foreground">{step.script}</code>
                          <CopyButton text={step.script} />
                        </div>
                        {step.script2 ? (
                          <div className="flex items-center gap-2 text-[13px]">
                            <span className="font-medium text-foreground">API Key:</span>
                            <code className="rounded border border-border bg-muted px-2 py-1 font-mono text-[12px] text-foreground">{step.script2}</code>
                            <CopyButton text={step.script2} />
                          </div>
                        ) : null}
                        <p className="text-[12px] text-muted-foreground">
                          Send user/account identify and track events to this endpoint (single event or batch array).
                        </p>
                        <CodeBlock
                          language="curl"
                          code={`https://events.hyperengage.io/api/v1/s2s/event?token=${data?.apiKey?.server_analytics_key || ""}`}
                        />
                      </div>
                    ) : (
                      <CodeBlock
                        code={step.script}
                        language={index === 0 ? (api ? "text" : "html") : "json"}
                      />
                    )}

                    {!api && step.script2 ? (
                      <CodeBlock code={step.script2} language="javascript" />
                    ) : null}

                    {index === 1 ? (
                      checkEvents?.data?.group?.length ? (
                        <CodeBlock
                          code={formatEventPreview(checkEvents.data.group[0])}
                          language="javascript"
                        />
                      ) : (
                        <WaitingEventCard text="Waiting to receive account events..." />
                      )
                    ) : null}

                    {index === 2 ? (
                      checkEvents?.data?.identify?.length ? (
                        <CodeBlock
                          code={formatEventPreview(checkEvents.data.identify[0])}
                          language="javascript"
                        />
                      ) : (
                        <WaitingEventCard text="Waiting to receive user events..." />
                      )
                    ) : null}

                    {index === 3 ? (
                      checkEvents?.data?.track?.length ? (
                        <CodeBlock
                          code={formatEventPreview(checkEvents.data.track[0])}
                          language="javascript"
                        />
                      ) : (
                        <WaitingEventCard text="Waiting to receive track events..." />
                      )
                    ) : null}

                    <div className="flex items-center justify-between pt-1">
                      <span className="text-[13px] text-muted-foreground">
                        Not working?{" "}
                        <a
                          href={DOC_LINKS[index] || DOC_LINKS[0]}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-primary hover:underline"
                        >
                          Open docs
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </span>

                      {index < steps.length - 1 ? (
                        <Button
                          type="button"
                          size="sm"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleNext();
                          }}
                        >
                          Next
                        </Button>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-end gap-3 border-t border-border pt-4">
        <Button type="button" disabled={!canContinue || !integration?.id} onClick={() => { void handleContinue(); }}>
          Continue
        </Button>
      </div>
    </div>
  );
};

export default NativeSteps;
